import React from 'react';
import { withNamespaces } from 'react-i18next';
import { observer } from 'mobx-react';
import { clientStore } from 'apps/client/app';
import { isInitiativeSlug } from 'utils';
import { CancelledPlanForTeam } from './CancelledPlanForTeam';

type PlanMonitorProps = {
  className?: string;
  style?: React.CSSProperties;
  noPermissionView?: React.ReactNode;
  children: React.ReactChild;
};

const workspaceRoutesName = [
  'workspace',
  'workspace.spaceFiles',
  'workspace.spaceProjects',
  'workspace.rfqs',
  'workspace.quotes',
  'workspace.orders',
  'workspace.spaceSettings.billing',
];

/**
 * If the plan is already canceled, canDelete is true, direct to /settings/billing,
 * else render canceled page for editors/viewers
 */
export const PlanMonitor = withNamespaces()(
  observer(({ children, noPermissionView }: PlanMonitorProps) => {
    const { activeSpace, routerStore, currentSpaceData, isStaff } = clientStore;

    if (!activeSpace || !currentSpaceData || clientStore.atCommunityRoute) {
      return children;
    }

    const isInitiativeSpace = isInitiativeSlug(
      clientStore.slugFromRouteOrSpace,
    );

    if (!isInitiativeSpace) return children;

    const { content } = activeSpace;

    // hasProSubscription is false, plan was canceled, else plan is active
    const { hasProSubscription } = currentSpaceData;
    const { canDelete } = content;

    const isAdminAndPlanWasCanceled = canDelete && !hasProSubscription;

    const canceledPage = noPermissionView || (
      <CancelledPlanForTeam
        isAdminAndPlanWasCanceled={isAdminAndPlanWasCanceled}
      />
    );

    const notInTopLevelRouterAndNoPermission =
      !workspaceRoutesName.some(r => r === routerStore.route.name) &&
      routerStore.route.name.startsWith('workspace') && // this is a workaround fix, all this code/approach is not right whatever the intention was
      !hasProSubscription;

    if (notInTopLevelRouterAndNoPermission) {
      const { spacePath, initiative } = routerStore.route.params;
      routerStore.navigate('workspace', {
        spacePath: activeSpace.path || initiative || spacePath,
      });

      // avoid cause page flashing before navigate to top page
      return children;
    }

    // non admin should render cancelled status when plan was cancelled
    const shouldRenderCanceledPage =
      (!hasProSubscription && !canDelete) || isAdminAndPlanWasCanceled && !isStaff;

    return shouldRenderCanceledPage ? canceledPage : children;
  }),
);
